import { DownloadIcon } from '@radix-ui/react-icons'
import {
	type ActionFunctionArgs,
	type LoaderFunctionArgs,
	type MetaFunction,
	data,
	redirect,
	useLoaderData,
} from 'react-router'
import {
	ErrorMessage,
	EventDetailLayout,
	GeneralErrorBoundary,
} from '#app/components/templates'
import { canUserEditEvent } from '#app/domains/performer'
import { EVENT_PRICE_SELECT, TICKET_TYPES } from '#app/domains/pricing/pure'
import {
	eventSummary,
	formatClockSpan,
	formatDayMonth,
	formatWeekday,
	SHARE_CARD_FORMATS,
	SHARE_CARD_SIZE,
	type ShareCardFormat,
	shareCardPath,
} from '#app/domains/program/pure'
import {
	checkTicketSale,
	reserveTickets,
	SEATS_TAKEN_COUNT,
	type TicketType,
} from '#app/domains/tickets'
import { isVippsConfigured } from '#app/domains/payments'
import { getUserId } from '#app/utils/auth.server.ts'
import { prisma } from '#app/utils/db.server.js'
import { pageMeta } from '#app/utils/page-meta.ts'
import { requireYearVisible } from '#app/utils/maintenance.server.ts'
import { useRequestInfo } from '#app/utils/request-info.ts'
import { useOptionalUser } from '#app/utils/user.ts'
import { slugify } from '#app/utils/slug.ts'
import { isIOSDevice } from '#app/utils/userAgent.ts'
import eventCardStyleUrl from '../../styles/event-card.css?url'
import eventPageStyleUrl from '../../styles/event-page.css?url'
import festivalStyleUrl from '../../styles/festival.css?url'
import stepperStyleUrl from '../../styles/stepper.css?url'
import { useRevalidateOnFocus } from '#app/utils/use-revalidate-on-focus.ts'

export const links = () => [
	{ rel: 'stylesheet', href: eventPageStyleUrl },
	{ rel: 'stylesheet', href: festivalStyleUrl },
	{ rel: 'stylesheet', href: eventCardStyleUrl },
	{ rel: 'stylesheet', href: stepperStyleUrl },
]

/** "?adultQty=2&childQty=1" → { adult: 2, child: 1 } (the picker's pre-fill) */
function quantitiesFrom(params: URLSearchParams | FormData) {
	const quantities: Partial<Record<TicketType, number>> = {}
	for (const type of TICKET_TYPES) {
		const raw = params.get(`${type}Qty`)
		if (typeof raw !== 'string' || raw.trim() === '') continue
		const parsed = Number.parseInt(raw, 10)
		if (!Number.isNaN(parsed) && parsed > 0) quantities[type] = parsed
	}
	return quantities
}

/**
 * Marks the link back from logging in ("?…&fra=innlogging"): the buyer
 * already pressed "Legg i korga" before logging in, so the page finishes
 * that press by itself (see DetailCtaCard's `addOnArrival`).
 */
const FROM_LOGIN = { key: 'fra', value: 'innlogging' } as const

export async function loader({ request, params }: LoaderFunctionArgs) {
	const eventId = params.eventId
	const isIOS = isIOSDevice(request)
	// seat COUNTS only: ticket rows carry door-pass hashes and holder names
	const lookup = await prisma.event.findUnique({
		where: { id: eventId },
		select: {
			id: true,
			date: true,
			endsAt: true,
			dropIn: true,
			title: true,
			description: true,
			shortDescription: true,
			performers: true,
			location: true,
			host: true,
			imageUrl: true,
			imageSafeZone: true,
			updatedAt: true,
			...EVENT_PRICE_SELECT,
			festivalId: true,
			seats: { select: { total: true } },
			_count: SEATS_TAKEN_COUNT,
			festival: {
				select: {
					year: true,
					datesText: true,
					mode: true,
					gateReleaseAt: true,
					isCurrent: true,
					pricingModel: true,
				},
			},
			featuring: {
				select: {
					id: true,
					slug: true,
					name: true,
					shortBio: true,
					imageUrl: true,
					imageSafeZone: true,
					updatedAt: true,
				},
			},
		},
	})
	const festival = lookup?.festival ?? null
	// an earlier year's concert closes with the site, and keeps its own gate
	await requireYearVisible(request, festival)
	if (!lookup) throw new Response('Fann ikkje konserten', { status: 404 })

	const { _count, ...rest } = lookup
	const event = { ...rest, ticketCount: _count.tickets }

	// this buyer's tickets for this concert: still in the cart (not yet
	// settled in Kassa), or already theirs (held for the door, or paid)
	const userId = await getUserId(request)
	const [inCart, reserved] = userId
		? await Promise.all([
				prisma.ticket.count({
					where: {
						eventId: lookup.id,
						buyerId: userId,
						paymentStatus: 'PENDING',
						settlement: 'ONLINE',
					},
				}),
				prisma.ticket.count({
					where: {
						eventId: lookup.id,
						buyerId: userId,
						OR: [
							{ paymentStatus: 'PAID' },
							{ paymentStatus: 'PENDING', settlement: 'DOOR' },
						],
					},
				}),
			])
		: [0, 0]

	const searchParams = new URL(request.url).searchParams
	const initialQuantities = quantitiesFrom(searchParams)
	// the share card in every size, for whoever posts the concert
	const canEdit = userId ? await canUserEditEvent(request, lookup.id) : false

	return {
		event,
		isIOS,
		festival,
		inCart,
		reserved,
		vippsOnline: isVippsConfigured(),
		initialQuantities,
		// back from logging in with tickets picked: add them now, once
		addOnArrival:
			Boolean(userId) &&
			searchParams.get(FROM_LOGIN.key) === FROM_LOGIN.value &&
			Object.keys(initialQuantities).length > 0,
		shareImage: shareCardPath(lookup),
		shareCards: canEdit
			? SHARE_CARD_DOWNLOADS.map((card) => ({
					...card,
					href: shareCardPath(lookup, card.format),
					...SHARE_CARD_FORMATS[card.format],
				}))
			: null,
	}
}

/**
 * Add-to-cart: the buyer picks a quantity per type (no names). Each ticket is
 * created as a held PENDING ticket immediately, and the buyer stays on the
 * concert: the card lists what was added (per type, from here) and offers
 * Kassen (/tickets/checkout) or more concerts, so ordering for several needs
 * no trip through Kassen between them. The sale rules are MCP's too
 * (`checkTicketSale`); only PER_CONCERT festivals sell here (ADR 0003), and
 * unset types are never offered and rejected here.
 * Not logged in yet? Log in (by e-mail code, or a provider) and come back to
 * the same choice, pre-filled, marked as a press to finish: the page then
 * posts it here once by itself (without JavaScript, the buyer presses again,
 * and goes on to Kassen).
 */
export async function action({ request, params }: ActionFunctionArgs) {
	const eventId = params.eventId
	const formData = await request.formData()
	const userId = await getUserId(request)
	if (!userId) {
		const back = new URLSearchParams()
		for (const [type, count] of Object.entries(quantitiesFrom(formData))) {
			back.set(`${type}Qty`, String(count))
		}
		back.set(FROM_LOGIN.key, FROM_LOGIN.value)
		const redirectTo = `/events/${eventId}?${back.toString()}#billetter`
		throw redirect(`/login?redirectTo=${encodeURIComponent(redirectTo)}`)
	}
	const event = await prisma.event.findUnique({
		where: { id: eventId },
		select: {
			id: true,
			festival: {
				select: {
					mode: true,
					gateReleaseAt: true,
					isCurrent: true,
					pricingModel: true,
				},
			},
		},
	})
	// same gate as the page: until the site and its festival are LIVE, only
	// staff can buy
	await requireYearVisible(request, event?.festival)
	if (!event) throw new Response('Not Found', { status: 404 })

	const qty = (key: string) => {
		const raw = formData.get(key)
		if (typeof raw !== 'string' || raw.trim() === '') return 0
		const parsed = Number.parseInt(raw, 10)
		return Number.isNaN(parsed) || parsed < 0 ? 0 : parsed
	}
	const wanted: Array<{ type: TicketType; count: number }> = TICKET_TYPES.map(
		(type) => ({ type, count: qty(`${type}Qty`) }),
	)
	// passed concerts, other festivals, unset prices and more tickets than
	// seats never sell, here or through MCP (a hidden festival is turned away
	// above, with the page's own gate). The last one also bounds the
	// quantities before a ticket is built per unit below.
	const sale = await checkTicketSale(
		prisma,
		userId,
		event.id,
		wanted.filter((w) => w.count > 0),
	)
	if (!sale.success) return data({ error: sale.error }, { status: 400 })
	// the picker's tickets are unnamed, and a legacy TIERED/SIMPLE festival
	// could never price or charge those: only PER_CONCERT sells here
	if (event.festival?.pricingModel !== 'PER_CONCERT') {
		return data(
			{ error: 'Billettane er ikkje i sal enno — prisane kjem snart' },
			{ status: 400 },
		)
	}
	const requests = wanted.flatMap((w) =>
		Array.from({ length: w.count }, () => ({ type: w.type })),
	)
	if (requests.length === 0) {
		return data({ error: 'Vel minst éin billett' }, { status: 400 })
	}
	const result = await reserveTickets(prisma, userId, event.id, requests)
	if (!result.success) {
		return data({ error: result.error }, { status: 400 })
	}
	// the page's own posts stay on the concert; a plain form post (no
	// JavaScript) goes on to Kassen, so a reload never posts the order twice
	if (formData.get('stay') !== '1') throw redirect('/tickets/checkout')
	return data({
		added: requests.length,
		lines: wanted.filter((w) => w.count > 0),
	})
}

export const meta: MetaFunction<typeof loader> = (args) => {
	const loaderData = args.loaderData
	if (!loaderData?.event) {
		return pageMeta(args, { title: 'Fann ikkje konserten' })
	}

	const { event, shareImage } = loaderData
	const when = `${formatWeekday(event.date)} ${formatDayMonth(event.date)} kl. ${formatClockSpan(event.date, event.endsAt)}`
	return pageMeta(args, {
		title: event.title,
		description: eventSummary(event, 200) || 'Huskonsert på Vorrå',
		image: {
			url: shareImage,
			...SHARE_CARD_SIZE,
			alt: `${event.title} — ${when}`,
			type: 'image/jpeg',
		},
	})
}

/** The share card's sizes on offer to download, and what each is for. */
const SHARE_CARD_DOWNLOADS: Array<{
	format: ShareCardFormat
	label: string
	use: string
	/** the end of the downloaded file's name */
	suffix: string
}> = [
	{
		format: '4x5',
		label: 'Instagram-innlegg',
		use: 'Ståande, 4:5',
		suffix: 'instagram',
	},
	{
		format: '9x16',
		label: 'Story og Reels',
		use: 'Instagram og Facebook, 9:16',
		suffix: 'story',
	},
	{
		format: 'link',
		label: 'Facebook-innlegg',
		use: 'Liggjande, same bilete som lenkja viser',
		suffix: 'facebook',
	},
]

/**
 * Poster sanity: the poster is a fixed A4 sheet and clips long descriptions.
 * ~1 100 characters (short lines/wrap included) is a safe budget for the
 * text column once title/facts/footer are laid out.
 */
const DESCRIPTION_LIMIT = 1100

export default function Event() {
	const {
		event,
		isIOS,
		inCart,
		reserved,
		vippsOnline,
		initialQuantities,
		addOnArrival,
		shareCards,
	} = useLoaderData<typeof loader>()
	// seats left stay current when a visitor comes back to the tab
	useRevalidateOnFocus()
	const { origin } = useRequestInfo()
	// the print poster is a tool for whoever manages the marketing
	const user = useOptionalUser()
	const isAdmin = user?.roles.some((role) => role.name === 'admin') ?? false
	const descriptionLength = (event.description ?? '').length
	const isDescriptionTooLong = descriptionLength > DESCRIPTION_LIMIT

	return (
		<>
			<main className="event-detail-main">
				<EventDetailLayout
					event={
						event as unknown as Parameters<typeof EventDetailLayout>[0]['event']
					}
					isIOS={isIOS}
					initialQuantities={initialQuantities}
					addOnArrival={addOnArrival}
					inCart={inCart}
					reserved={reserved}
					vippsOnline={vippsOnline}
					calendarUrl={`/events/${event.id}/kalender.ics`}
					shareUrl={`${origin}/events/${event.id}`}
				/>
			</main>

			{shareCards ? (
				<ShareCardDownloads title={event.title} cards={shareCards} />
			) : null}

			{isAdmin ? (
				<div className="mx-auto my-6 flex max-w-3xl flex-col gap-3 px-4">
					<p className="text-sm">
						Vil du ha ein plakat til utskrift?{' '}
						<a
							className="link"
							href={`/resources/print.pdf?kind=poster&eventId=${event.id}`}
							download
						>
							Last ned som PDF
						</a>{' '}
						(A4, ståande).
					</p>
					{isDescriptionTooLong ? (
						<p className="alert alert-warning text-sm" role="status">
							Omtalen er truleg for lang til éi plakatside, og teksten blir
							klipt med «Les meir». ({descriptionLength} teikn, tilrådd under{' '}
							{DESCRIPTION_LIMIT})
						</p>
					) : null}
				</div>
			) : null}
		</>
	)
}

/**
 * The share card in each size, ready to post: shown to whoever may edit the
 * concert. Each picture is drawn from the concert as it is now.
 */
function ShareCardDownloads({
	title,
	cards,
}: {
	title: string
	cards: Array<
		(typeof SHARE_CARD_DOWNLOADS)[number] & {
			href: string
			width: number
			height: number
		}
	>
}) {
	return (
		<section
			aria-labelledby="share-cards-title"
			className="mx-auto mt-10 max-w-3xl px-4"
		>
			<h2 id="share-cards-title" className="ev-section-title">
				Bilete til sosiale medium
			</h2>
			<p className="mt-1 text-sm">
				Ferdige bilete av arrangementet, klare til å dele. Dei viser det slik
				det står her no, så last dei ned på nytt om du endrar noko.
			</p>
			<ul className="mt-4 grid gap-3 sm:grid-cols-3">
				{cards.map((card) => (
					<li key={card.format}>
						<a
							href={card.href}
							download={`${slugify(title) || 'konsert'}-${card.suffix}.jpg`}
							className="flex h-full items-center gap-4 rounded-lg border border-black/10 p-3 hover:bg-black/5 sm:flex-col sm:items-start"
						>
							<span className="flex h-24 w-24 shrink-0 items-center justify-center sm:h-40 sm:w-full">
								<img
									src={card.href}
									alt=""
									width={card.width}
									height={card.height}
									loading="lazy"
									className="h-auto max-h-full w-auto max-w-full rounded shadow-sm"
								/>
							</span>
							<span className="flex flex-col gap-0.5">
								<span className="font-semibold">{card.label}</span>
								<span className="text-sm opacity-75">
									{card.use} · {card.width} × {card.height}
								</span>
								<span className="link mt-1 inline-flex items-center gap-1 text-sm">
									<DownloadIcon aria-hidden="true" /> Last ned
								</span>
							</span>
						</a>
					</li>
				))}
			</ul>
		</section>
	)
}

export function ErrorBoundary() {
	return (
		<GeneralErrorBoundary
			statusHandlers={{
				404: () => (
					<ErrorMessage
						code={404}
						title="Fann ikkje konserten"
						text="Kanskje lenkja er gamal, eller konserten er flytta. Programmet har alle konsertane."
					/>
				),
			}}
		/>
	)
}
